import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPatch, apiPost, apiPut } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'

/**
 * The Support tab's API. The backend owns every rule (the clock, deadlines, who may do what);
 * this file only reads what it says and sends the buttons' intents.
 */

/** Priorities P1–P4 and REQ (Product Enhancement & Requests). The backend sends only these codes. */
export type Severity = 'P1' | 'P2' | 'P3' | 'P4' | 'REQ'
export type CaseState = 'triage' | 'helpdesk' | 'engineering' | 'restored' | 'fixed' | 'closed' | 'dismissed'
export type Milestone = 'response' | 'triage' | 'pickup' | 'restore' | 'fix'
export type MilestoneStatus = 'met' | 'missed' | 'on_track' | 'at_risk' | 'breached' | 'paused'
export type QueueView = 'mine' | 'triage' | 'all' | 'closed'

export const SEVERITY_LABEL: Record<Severity, string> = { P1: 'P1 Critical', P2: 'P2 High', P3: 'P3 Medium', P4: 'P4 Low', REQ: 'Product Enhancement & Requests' }
/** Short form for badges and tight rows. */
export const SEVERITY_SHORT: Record<Severity, string> = { P1: 'P1 Critical', P2: 'P2 High', P3: 'P3 Medium', P4: 'P4 Low', REQ: 'Enhancement / Request' }
export const SEVERITY_HINT: Record<Severity, string> = {
  P1: 'Platform unavailable',
  P2: 'Customer workflow completely blocked',
  P3: 'Major functionality affected',
  P4: 'Minor bug',
  REQ: 'Enhancement or feature request',
}
/** Events written before the rename name S1–S4; show them as P1–P4. */
export function priorityName(code: string | null | undefined): string {
  if (!code) return 'untriaged'
  const legacy: Record<string, string> = { S1: 'P1', S2: 'P2', S3: 'P3', S4: 'P4' }
  return legacy[code] ?? code
}
export const MILESTONE_LABEL: Record<Milestone, string> = {
  response: 'First response',
  triage: 'Triage',
  pickup: 'Engineering pickup',
  restore: 'Restore',
  fix: 'Fix',
}
/**
 * What a milestone's deadline is waiting for, in plain words, for people who have not read the
 * framework: shown under the lateness wherever a ticket's next deadline appears.
 */
export const MILESTONE_PENDING: Record<Milestone, string> = {
  response: 'No reply to the customer yet',
  triage: 'Not triaged yet',
  pickup: 'No one has picked it up',
  restore: "Customer still can't work",
  fix: 'Not fixed yet',
}

/** "45 min", "5h 20m", "1 day 4h", "6 days": rounded the way people say it. */
export function plainDuration(ms: number): string {
  const m = Math.max(1, Math.round(Math.abs(ms) / 60_000))
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`
  const d = Math.floor(h / 24)
  if (d < 2) return h % 24 ? `1 day ${h % 24}h` : '1 day'
  return `${d} days`
}

/** "6 days late" past the deadline, "due in 2h 15m" before it. */
export const lateness = (msLeft: number) => (msLeft < 0 ? `${plainDuration(msLeft)} late` : `due in ${plainDuration(msLeft)}`)

/** The exact deadline, for a tooltip: "Restore was due Mon 19 Oct, 9:26 AM". */
export function deadlineTitle(milestone: Milestone, deadlineAt: string | null, now: number): string {
  if (!deadlineAt) return MILESTONE_LABEL[milestone]
  const at = Date.parse(deadlineAt)
  const when = new Date(at).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
  return `${MILESTONE_LABEL[milestone]} ${at < now ? 'was due' : 'is due'} ${when}`
}

export const STATE_LABEL: Record<CaseState, string> = {
  triage: 'Needs triage',
  helpdesk: 'With helpdesk',
  engineering: 'With engineering',
  restored: 'Restored',
  fixed: 'Fixed · confirming',
  closed: 'Closed',
  dismissed: 'Dismissed',
}

export interface MilestoneView {
  milestone: Milestone
  target: number
  startedAt: string
  deadlineAt: string | null
  doneAt: string | null
  status: MilestoneStatus
  remaining: number | null
}

export interface DeskEvent {
  id: string
  at: string
  by: string
  byName: string
  kind: string
  text?: string
  from?: string | null
  to?: string | null
  customerVisible?: boolean
  sent?: boolean
  eta?: string | null
  evidence?: string | null
  /** Snoozed or blocked: reminders are held until then. */
  until?: string | null
}

export interface RecoveryEta {
  at: string
  reason: string
  by: string
  byName: string
  setAt: string
}

export interface UpdateView {
  lastAt: string
  dueAt: string
  status: 'on_track' | 'at_risk' | 'overdue'
  remaining: number
}

export interface SupportCase {
  id: string
  sourceId: string
  sourceKind: 'ticket' | 'conversation' | 'manual'
  channel: string
  reference: string
  title: string
  summary: string | null
  clientId: string | null
  clientName: string | null
  requester: string | null
  product: string | null
  lifecycle: string | null
  severity: Severity | null
  suggestedSeverity: Severity | null
  suggestedTeam: string | null
  suggestionReason: string | null
  state: CaseState
  ownerTeam: string | null
  assigneeId: string | null
  firstContactAt: string
  firstContactSetBy: string | null
  firstResponseAt: string | null
  triagedAt: string | null
  routedAt: string | null
  pickedUpAt: string | null
  restoredAt: string | null
  fixedAt: string | null
  closedAt: string | null
  waitingSince: string | null
  nextDeadlineAt: string | null
  nextDeadlineKind: Milestone | null
  jiraKey: string | null
  githubUrl: string | null
  resolutionNote: string | null
  rootCause: string | null
  reopenedCount: number
  clock: { milestones: MilestoneView[]; next: MilestoneView | null; health: 'on_track' | 'at_risk' | 'breached' | 'paused' | 'done'; breached: Milestone[] }
  ageMs: number
  waiting: boolean
  /** Closed in Intercom while engineering had it; nobody has recorded how it ended yet. */
  outcomeNeeded?: boolean
  /** The customer update clock (30 min P1, 2h P2, daily P3), when it applies. */
  update: UpdateView | null
  /** When we started waiting on the customer (a question, or a fix awaiting confirmation). */
  waitingFrom: string | null
  /** Reminders are held until then ("Remind me in 2h" or "Blocked"); null when not snoozed. */
  snoozedUntil?: string | null
  /** A customer update drafted from engineering's notes, for helpdesk to review and send. */
  draft?: { text: string; at: string } | null
  /** The recurring theme and the kind of fix it needs (Insights). */
  theme?: TicketTheme | null
  eta: RecoveryEta | null
  lastUpdateAt: string | null
  events: DeskEvent[]
  related?: Array<{ caseId: string; kind: RelationKind; at: string; by: string; byName: string }>
}

/** How another ticket relates to this one, from this ticket's side. The backend writes both sides. */
export type RelationKind = 'duplicate_of' | 'duplicates' | 'related' | 'part_of_incident' | 'incident'
export const RELATION_LABEL: Record<RelationKind, string> = {
  duplicate_of: 'Duplicate of',
  duplicates: 'Has duplicate',
  related: 'Related',
  part_of_incident: 'Part of incident',
  incident: 'Incident includes',
}
/** The choices offered when linking: the other three are the mirrored side. */
export const RELATION_CHOICES: Array<{ id: RelationKind; label: string; hint: string }> = [
  { id: 'duplicate_of', label: 'This is a duplicate of…', hint: 'Same report twice. Work happens on the other ticket.' },
  { id: 'related', label: 'Related to…', hint: 'Same issue or area, both tickets stay open.' },
  { id: 'part_of_incident', label: 'Part of the incident on…', hint: 'Group tickets from one outage (usually a P1) under one ticket.' },
]

export interface RelatedTicket {
  caseId: string
  kind: RelationKind
  at: string
  byName: string
  reference: string
  title: string
  state: CaseState
  severity: Severity | null
  clientName: string | null
  ownerTeam: string | null
}

export type ActivityKind = 'message' | 'slack' | 'intercom' | 'intercom_note' | 'github_comment' | 'github_pr' | 'github_issue' | 'resolution'

export interface ActivityFile {
  id: string
  name: string
  contentType: string
  size: number | null
  /** Files held elsewhere (Intercom) open directly; Tapwork uploads are fetched through the API. */
  url: string | null
}

export interface ResolutionSummary {
  problem: string
  fix: string
  cause: string
  prevention: string
}

export interface ActivityItem {
  id: string
  caseId: string
  kind: ActivityKind
  source: 'tapwork' | 'slack' | 'intercom' | 'github'
  authorId: string | null
  authorName: string | null
  authorKind: string | null
  body: string
  files: ActivityFile[]
  mentions: string[]
  visibility: 'internal' | 'customer'
  at: string
  link: string | null
  resolution: ResolutionSummary | null
}

export interface ActivityFeed {
  items: ActivityItem[]
  capture: { intercom: string; github: string }
}

export interface DeskPerson {
  id: string
  displayName: string
  email: string
  photoUrl: string | null
  isLead: boolean
}

export interface DeskSettings {
  startAt: string | null
  slaChannelId: string | null
  warRoomChannelId: string | null
  helpdeskMemberIds: string[]
  helpdeskLeadId: string | null
  escalationHeadId: string | null
  ceoUserId: string | null
  productLeadId: string | null
  helpdeskChannelId: string | null
  autoReplies: boolean
  intercomAdminId: string | null
  slackEnabled: boolean
  /** The older ticket pages are read-only; the Support queue is the one place. */
  legacyFrozen?: boolean
  helpdeskAwayIds?: string[]
  teamAlertsInSla?: boolean
}

export interface DeskMeta {
  teams: Array<{ name: string; members: DeskPerson[] }>
  severities: Array<{ id: Severity; label: string; short: string; definition: string; targets: Record<Milestone, number | null> }>
  rootCauses: string[]
  dismissReasons: string[]
  settings: DeskSettings
}

type Pct = number | null
export interface DeskDashboard {
  generatedAt: string
  range: { from: string; to: string }
  kpis: {
    open: number
    untriaged: number
    oldestUntriagedMs: number | null
    atRisk: number
    breachedOpen: number
    slaMetPct: Pct
    restoreMedianMs: number | null
    restoreP90Ms: number | null
    responseMedianMs: number | null
    responseP90Ms: number | null
    created: number
    resolved: number
    resolutionRate: Pct
    updatesOverdue: number
    waitingReview: number
    /** Closed in Intercom while engineering had them, with no outcome recorded. */
    outcomeNeeded?: number
  }
  bySeverity: Array<{ severity: Severity | 'untriaged'; open: number; atRisk: number; breached: number; slaMetPct: Pct; met: Record<Milestone, Pct> }>
  byTeam: Array<{ team: string; open: number; unclaimed: number; atRisk: number; breached: number; slaMetPct: Pct; decided: number; oldestOpenMs: number | null; flag: 'green' | 'amber' | 'red' }>
  byClient: Array<{ clientId: string | null; clientName: string; lifecycle: string | null; open: number; created: number; breaches: number; s1s2: number; flag: 'green' | 'amber' | 'red' }>
  byProduct: Array<{ product: string; created: number; open: number; slaMetPct: Pct }>
  aging: Array<{ bucket: string; total: number } & Record<string, number | string>>
  causes: Array<{ cause: string; count: number }>
  trend: Array<{ day: string; opened: number; closed: number; backlog: number; slaMetPct: Pct }>
  nextToBreach: Array<{ id: string; reference: string; title: string; severity: Severity | null; clientName: string | null; team: string; assigneeId: string | null; milestone: Milestone; deadlineAt: string | null; status: MilestoneStatus; remainingMs: number | null }>
}

export interface IntercomAttachment {
  id: string
  name: string
  contentType: string
  url: string
  size: number | null
}

export interface IntercomPart {
  id: string
  partType: string
  body: string
  attachments?: IntercomAttachment[]
  createdAt: string | null
  author: { type: string | null; name: string | null; email: string | null }
}

/** The full Intercom record behind a ticket: what the customer reported, who they are, and every message. */
export interface IntercomDetail {
  ticketId: string
  title: string
  description: string
  category: string | null
  stateLabel: string | null
  ticketType: string | null
  createdAt: string | null
  contacts: Array<{ id: string; name: string | null; email: string | null; companyName?: string | null }>
  attributes: Record<string, string>
  parts: IntercomPart[]
}

// ───────────── reads ─────────────

export const useDeskCases = (view: QueueView, filters: Record<string, string>) =>
  useQuery({
    queryKey: qk.supportDeskCases(view, JSON.stringify(filters)),
    queryFn: () => apiGet<{ items: SupportCase[]; total: number }>('support-desk/cases', { view, ...filters }),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })

export const useDeskCase = (id: string | null) =>
  useQuery({ queryKey: qk.supportDeskCase(id ?? ''), queryFn: () => apiGet<SupportCase>(`support-desk/cases/${id}`), enabled: Boolean(id), refetchInterval: 30_000 })

export const useDeskMeta = (enabled = true) => useQuery({ queryKey: qk.supportDeskMeta(), queryFn: () => apiGet<DeskMeta>('support-desk/meta'), staleTime: 5 * 60_000, enabled })

export const useDeskDashboard = (from: string, to: string) =>
  useQuery({ queryKey: qk.supportDeskDashboard(from, to), queryFn: () => apiGet<DeskDashboard>('support-desk/dashboard', { from, to }), refetchInterval: 60_000 })

/** The customer conversation, read live from Intercom through the existing support endpoint. */
export const useConversation = (sourceId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: qk.supportDeskConversation(sourceId ?? ''),
    queryFn: () => apiGet<IntercomDetail>(`support-tickets/${encodeURIComponent(sourceId!)}`),
    enabled: Boolean(sourceId) && enabled,
    staleTime: 60_000,
    retry: false,
  })

/** The ticket's activity feed; reading it also copies in the latest Intercom and GitHub messages. */
export const useDeskActivity = (id: string | null) =>
  useQuery({ queryKey: qk.supportDeskActivity(id ?? ''), queryFn: () => apiGet<ActivityFeed>(`support-desk/cases/${id}/activity`), enabled: Boolean(id), refetchInterval: 30_000 })

export const useDeskRelated = (id: string | null, enabled = true) =>
  useQuery({ queryKey: qk.supportDeskRelated(id ?? ''), queryFn: () => apiGet<{ items: RelatedTicket[] }>(`support-desk/cases/${id}/related`), enabled: Boolean(id) && enabled })

export const activityFileUrl = (caseId: string, activityId: string, fileId: string, view?: 'inline' | 'download') =>
  apiGet<{ url: string }>(`support-desk/cases/${caseId}/activity/${activityId}/files/${encodeURIComponent(fileId)}`, view ? { view } : undefined).then((r) => r.url)

export const draftResolution = (id: string) => apiPost<ResolutionSummary>(`support-desk/cases/${id}/resolution-draft`, {})

export interface DeskExport {
  columns: string[]
  rows: string[][]
  filename: string
}
export const fetchDeskExport = (view: string, range: { from?: string; to?: string } = {}) => apiGet<DeskExport>('support-desk/export', { view, ...range })

/** What needs the caller now, for the sidebar badge: theirs and at risk or breached, or waiting for triage. */
export const useDeskBadge = (enabled: boolean) =>
  useQuery({
    queryKey: qk.supportDeskCases('mine', 'badge'),
    queryFn: () => apiGet<{ items: SupportCase[] }>('support-desk/cases', { view: 'mine' }),
    select: (data) => data.items.filter((c) => c.state === 'triage' || c.clock.health === 'breached' || c.clock.health === 'at_risk').length,
    refetchInterval: 60_000,
    enabled,
  })

// ───────────── writes ─────────────

export function useDeskAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: string; body?: unknown }) => apiPost<SupportCase>(`support-desk/cases/${id}/${action}`, body ?? {}),
    retry: false,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.supportDesk() }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function usePostActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, text, attachments }: { id: string; text: string; attachments: Array<{ key: string; name: string }> }) =>
      apiPost<ActivityItem>(`support-desk/cases/${id}/activity`, { text, attachments }),
    retry: false,
    onSuccess: (_r, v) => void queryClient.invalidateQueries({ queryKey: qk.supportDeskActivity(v.id) }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useDeskLinks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => apiPatch<SupportCase>(`support-desk/cases/${id}/links`, body),
    retry: false,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.supportDesk() }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useLogTicket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<SupportCase>('support-desk/cases', body),
    retry: false,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.supportDesk() }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useSaveDeskSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<DeskSettings>) => apiPut<DeskSettings>('support-desk/settings', body),
    retry: false,
    onSuccess: () => {
      toast.success('Support settings saved')
      void queryClient.invalidateQueries({ queryKey: qk.supportDeskMeta() })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useRunDesk() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<{ sync: { scanned: number; synced: number }; open: number }>('support-desk/run'),
    retry: false,
    onSuccess: (r) => {
      toast.success(`Synced ${r.sync.synced} of ${r.sync.scanned} recent Intercom items · ${r.open} open`)
      void queryClient.invalidateQueries({ queryKey: qk.supportDesk() })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

/** A GitHub issue link (not a pull request), as the backend requires when engineering marks a ticket fixed. */
export const GITHUB_ISSUE = /^https:\/\/github\.com\/[\w.-]+\/[\w.-]+\/issues\/\d+\/?(?:[?#].*)?$/

// ───────────── formatting ─────────────

export function formatDuration(ms: number): string {
  const m = Math.round(Math.abs(ms) / 60_000)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`
  const d = Math.floor(h / 24)
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`
}

export const severityTone = (s: Severity | null) => (s === 'P1' ? 'danger' : s === 'P2' ? 'warning' : s === 'P3' ? 'info' : s ? 'neutral' : 'accent') as 'danger' | 'warning' | 'info' | 'neutral' | 'accent'
export const statusTone = (s: MilestoneStatus | 'done') =>
  (s === 'breached' || s === 'missed' ? 'danger' : s === 'at_risk' ? 'warning' : s === 'met' || s === 'done' ? 'success' : s === 'paused' ? 'neutral' : 'brand') as 'danger' | 'warning' | 'success' | 'neutral' | 'brand'

// ───────────── the context pack ─────────────

export interface SimilarTicket {
  id: string
  reference: string
  title: string
  team: string | null
  rootCause: string | null
  resolutionNote: string | null
  fixedAt: string | null
}

export interface ContextPack {
  brief: { text: string; at: string } | null
  briefStatus: 'ok' | 'off' | 'failed'
  similar: SimilarTicket[]
  openForClient: Array<{ id: string; reference: string; title: string; severity: Severity | null; state: CaseState; team: string | null }>
}

/** What someone picking the ticket up needs first: a summary, tickets solved before, the customer's other open tickets. */
export const useDeskContext = (id: string) =>
  useQuery({
    queryKey: [...qk.supportDeskCase(id), 'context'],
    queryFn: () => apiGet<ContextPack>(`support-desk/cases/${id}/context`),
    staleTime: 5 * 60_000,
    retry: false,
  })

// ───────────── insights ─────────────

export type FixType = 'bug' | 'data' | 'config' | 'howto' | 'feature'
export const FIX_TYPE_LABEL: Record<FixType, string> = {
  bug: 'Product bug',
  data: 'Data correction',
  config: 'Configuration or setup',
  howto: 'How-to or training',
  feature: 'Feature gap',
}
export type InsightPeriod = 'today' | 'week' | 'month' | 'all'

export interface TicketTheme {
  name: string
  fixType: FixType
  by: string
  byName: string
  at: string
}

export interface ThemeFix {
  label: string
  url: string | null
  shippedAt: string | null
  by: string
  at: string
}

export interface DeskInsights {
  period: InsightPeriod
  window: { from: string; to: string }
  tagged: { done: number; total: number }
  headlines: Array<{ period: InsightPeriod; label: string; text: string; tone: 'green' | 'amber' | 'red' }>
  themes: Array<{
    theme: string
    fixType: FixType | null
    fixTypeLabel: string | null
    product: string | null
    count: number
    previous: number | null
    clients: string[]
    engineeringPct: number | null
    medianFixMs: number | null
    examples: Array<{ id: string; reference: string; title: string }>
    waitedHours: number
    pain: number
  }>
  fixTypes: Array<{ fixType: FixType; label: string; owner: string; count: number; pct: number }>
  fixFirst: Array<{ rank: number; theme: string; fixType: FixType | null; fixTypeLabel: string | null; product: string | null; tickets: number; waitedHours: number; perMonthHours: number; proposal: string; fix: ThemeFix | null }>
  impact: Array<{ theme: string; fix: ThemeFix; beforePerWeek: number; afterPerWeek: number; weeksAfter: number; verdict: 'worked' | 'no_change' | 'too_early' }>
  quality: { otherCausePct: number | null; otherCauseCount: number; noProduct: number; sentToEngineering: number; notCode: number; fcrPct?: number | null; closed?: number; reopened?: number }
}

export const useDeskInsights = (period: InsightPeriod) =>
  useQuery({
    queryKey: [...qk.supportDesk(), 'insights', period],
    queryFn: () => apiGet<DeskInsights>('support-desk/insights', { period }),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  })

export function useSaveThemeFix() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { theme: string; label: string | null; url: string | null; shippedAt: string | null }) => apiPut<{ fixes: Record<string, ThemeFix> }>('support-desk/insights/fix', body),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...qk.supportDesk(), 'insights'] })
      toast.success('Saved')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export const fetchReviewPack = (period: InsightPeriod) => apiGet<{ title: string; text: string }>('support-desk/insights/review-pack', { period })
